import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Worcestershire\'s GREEN overall DfT rating mean I cannot claim?',
    answer: 'No. GREEN overall means Worcestershire performs well on aggregate spend, best practice and much of its maintenance programme — but Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. The condition scorecard is AMBER, U-roads are assessed by CVI visual survey rather than SCANNER, and the council still filled 16,022 potholes in 2024/25 while preventative spend fell from 10% to a projected 8%.',
  },
  {
    question: 'Why is the DfT condition scorecard AMBER when overall is GREEN?',
    answer: 'The Department for Transport rates Worcestershire GREEN overall with GREEN spend and best-practice scorecards, but AMBER on condition. The council\'s own 2024 data shows A-road green condition falling from 74% to 67% in one year, and 22% of U-roads in red condition — roughly 455km of residential network. Aggregate scorecards do not prove the individual pothole on your street was known and repaired within inspection intervals.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 2,069km — 50.5% of Worcestershire\'s 4,101km carriageway network. The council assesses U-roads using CVI (Coarse Visual Inspection) surveys annually, not the SCANNER technology used on A and B/C roads. At the 2024 survey, 22% of U-roads were in red condition. CVI data may not capture defects as precisely as laser surveys — and most pothole damage happens on these residential roads.',
  },
  {
    question: 'Prevention spending fell from 10% to 8% — does that matter for my claim?',
    answer: 'It can provide context. Worcestershire\'s transparency report states that "proactive preventative measures are more cost-effective and less disruptive than reactive repairs", yet estimated preventative maintenance share fell from 10% in 2021/22 to a projected 8% in 2025/26 — a 20% relative decline. Pothole fills meanwhile rose from 13,622 in 2022/23 to 16,022 in 2024/25. That pattern does not excuse a specific unrepaired defect, but it shows reactive workload persisting alongside declining prevention share.',
  },
  {
    question: 'The council says over 75% of potholes get permanent cut-and-patch repairs — what about the rest?',
    answer: 'The transparency report states that "over 75% of identified potholes are repaired" by cutting out and patching. That means up to a quarter may receive lower-standard temporary repairs. If your vehicle was damaged by a defect that had been previously patched, or by a temporary repair that failed, the council\'s own admission about repair standards becomes relevant evidence alongside photos and prior reports.',
  },
  {
    question: 'Worcestershire spends more than double its DfT capital allocation — does that block my claim?',
    answer: 'Not automatically. In 2024/25 the council spent £50.5m capital against a DfT allocation of £21.1m — earning a GREEN spend scorecard. Projected 2025/26 capital spend is £52.6m against £29.6m DfT allocation. Section 58 requires proof that the specific defect was reasonably inspected and repaired, not proof of aggregate budget size. Prior reports, photos showing defect age, and your road\'s place in the council\'s survey regime matter more than headline spend.',
  },
  {
    question: 'How do I report a pothole to Worcestershire County Council?',
    answer: 'Report road and path issues via Worcestershire\'s online portal at worcestershire.gov.uk — choose "Pothole or damage to road or pavement surface". For highways emergencies outside office hours, call 01905 822822. Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function WorcestershireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Worcestershire Pothole Claims | 16,022 Repairs, Amber Condition | Fixtyer"
        description="Worcestershire County Council earns GREEN DfT ratings overall but AMBER on condition. 22% of U-roads are in red condition, preventative spend fell to 8%, and the council filled 16,022 potholes in 2024/25. What that means for your Section 58 claim."
        canonical="/councils/worcestershire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Worcestershire', url: '/councils/worcestershire' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Worcestershire County Council"
        councilSlug="worcestershire"
        rating="green"
      />
      <SpeakableSchema
        url="/councils/worcestershire"
        headline="Worcestershire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Worcestershire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="green" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            16,022 Pothole Repairs in 2024/25 on a Green-Rated County Network
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Worcestershire County Council earns <strong className="text-emerald-400">GREEN</strong> DfT scorecards
            for overall performance, spend and best practice — yet its condition scorecard is{' '}
            <strong className="text-amber-400">AMBER</strong>. The council filled{' '}
            <strong className="text-rose-400">16,022 potholes in 2024/25</strong>, preventative maintenance share
            fell from 10% to a projected 8%, and{' '}
            <strong className="text-amber-400">22% of U-roads (455km)</strong> were in red condition at the last CVI
            survey. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-emerald-400">£52.6m</div>
            <div className="text-white font-medium mt-2">Projected capital spend 2025/26</div>
            <div className="text-brand-300 text-sm mt-1">
              Against a DfT capital allocation of £29.6m — nearly 1.8× the grant — while preventative share is
              projected at 8% and the council still plans circa 16,000 pothole repairs this year.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            4,101km of Roads — Half Unclassified
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Worcestershire&apos;s June 2025 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">4,101km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">2,069km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">50.5% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">1,457km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">575km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">Motorways and A46 maintained by National Highways</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Worcestershire highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">3,347km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">1,805km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">4,630km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Bridges and structures</td>
                  <td className="px-6 py-4 text-center font-medium">1,462 structures</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Street lighting columns</td>
                  <td className="px-6 py-4 text-center font-medium">55,068</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="The highway network in Worcestershire is managed with a careful balance between carrying out long-term preventative maintenance repairs and reactive repairs. Reactive repairs are often necessary for safety and other reasons, but like many other authorities, we carry out a risk and databased asset management approach to reduce the need for short-term reactive work."
            source="Worcestershire County Council Local Highway Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What AMBER Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER surveys on classified roads — CVI visual surveys on U-roads where most claims start
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> A and B/C roads are surveyed annually using SCANNER technology.
              U-road data is derived from CVI (Coarse Visual Inspection) surveys, taking into account wearing course,
              structural and edge deterioration — a different method from the laser surveys used on classified roads.
              The council also considers reported defects, complaints, third-party claims and public satisfaction when
              assessing network condition.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (575km) — annual SCANNER surveys</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">74%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">29%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">67%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road green condition fell from 74% in 2023 to 67% in 2024 — a seven-point drop in one year, with
              amber rising to 29%. Principal roads are a minority of the network, but the deterioration trend is
              documented in the council&apos;s own SCANNER data.
            </p>
          </div>

          {/* B&C roads */}
          <div className="bg-white border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (1,457km) — annual SCANNER surveys</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">28%</td>
                    <td className="px-4 py-2 text-center">68%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">4%</td>
                    <td className="px-4 py-2 text-center font-bold">25%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">71%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C roads have held at 71% green since 2022 — stronger than A-roads. Classified roads total 2,032km
              (49.5% of the carriageway network).
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (2,069km) — where most claims start</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">19%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">20%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center text-rose-600">24%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">22%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              At 22% red condition in 2024, roughly 455km of Worcestershire&apos;s residential network requires
              maintenance — up from 19% in 2021. The transparency report publishes red percentages for U-roads via
              CVI survey; amber and green breakdowns are not separately tabulated in the published report.
            </p>
          </div>

          <QuoteCard
            quote="U Road data is derived from CVI condition Surveys (Coarse Visual Surveys), taking into account wearing course, structural and edge deterioration."
            source="Worcestershire County Council Local Highway Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN spend — but preventative share projected at 8% for 2025/26
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Worcestershire highway maintenance spending 2021-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-amber-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">29,571</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">52,647</td>
                  <td className="px-4 py-3 text-center">9,465</td>
                  <td className="px-4 py-3 text-center text-amber-400">8%</td>
                  <td className="px-4 py-3 text-center">3%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">21,121</td>
                  <td className="px-4 py-3 text-center">50,495</td>
                  <td className="px-4 py-3 text-center">8,286</td>
                  <td className="px-4 py-3 text-center">9%</td>
                  <td className="px-4 py-3 text-center">3%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">24,452</td>
                  <td className="px-4 py-3 text-center">50,111</td>
                  <td className="px-4 py-3 text-center">7,521</td>
                  <td className="px-4 py-3 text-center">9%</td>
                  <td className="px-4 py-3 text-center">3%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">18,738</td>
                  <td className="px-4 py-3 text-center">42,904</td>
                  <td className="px-4 py-3 text-center">7,606</td>
                  <td className="px-4 py-3 text-center">10%</td>
                  <td className="px-4 py-3 text-center">4%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">18,738</td>
                  <td className="px-4 py-3 text-center">44,329</td>
                  <td className="px-4 py-3 text-center">6,921</td>
                  <td className="px-4 py-3 text-center">10%</td>
                  <td className="px-4 py-3 text-center">4%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is GREEN</h3>
              <p className="text-brand-200 text-sm">
                Capital spend has risen from £42.9m in 2022/23 to a projected £52.6m in 2025/26, while the council
                consistently invests well beyond its DfT capital allocation. Total 2024/25 maintenance spend
                (capital plus revenue) reached approximately £58.8m — nearly 2.8× the DfT grant alone.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why claims still happen</h3>
              <p className="text-brand-200 text-sm">
                Despite substantial investment, preventative maintenance share has fallen from 10% to a projected 8%
                while pothole fills rose to 16,022 in 2024/25. The council states prevention is more cost-effective
                than reactive work — yet the budget mix has shifted the other way. Spend volume does not prove every
                defect was caught within inspection intervals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Rising Pothole Repair Counts
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — from Worcestershire&apos;s June 2025 transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Worcestershire estimated potholes filled 2020-2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2022/23</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">15,877</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">14,445</td>
                  <td className="px-6 py-4 text-center text-emerald-600">−5.9%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">13,622</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">15,557</td>
                  <td className="px-6 py-4 text-center text-amber-600">+14.2%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">16,022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+17.6%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (planned)</td>
                  <td className="px-6 py-4 text-center font-bold">~16,000</td>
                  <td className="px-6 py-4 text-center text-surface-500">Council estimate</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="Where possible repairs to potholes are repaired to the highest standard by cutting it out and using a patch. Over 75% of identified potholes are repaired like this."
              source="Worcestershire County Council Local Highway Maintenance Transparency Report (June 2025)"
            />
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Nearly 44 repairs a day</h3>
              <p className="text-surface-600 text-sm">
                16,022 pothole repairs in 2024/25 works out to roughly 44 per day across 4,101km. A green-rated
                county still producing that volume of safety-critical defects is one where individual potholes
                routinely form between inspections — especially on U-roads assessed by CVI rather than SCANNER.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Prevention paradox */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Prevention Paradox
          </h2>
          <p className="text-surface-600 mb-8">
            The council acknowledges prevention works — yet preventative share has declined
          </p>

          <QuoteCard
            quote="Proactive preventative measures are more cost-effective and less disruptive to highway users than reactive repairs. Road surfaces and structures are maintained routinely in good condition to prevent major deterioration and the formation of potholes, which are costly and disruptive to fix."
            source="Worcestershire County Council Local Highway Maintenance Transparency Report (June 2025)"
          />

          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-6">
            <p className="text-amber-800 text-sm">
              Since 2021, Worcestershire has surface-dressed over 800km of carriageways as preventative maintenance.
              In the last five years it has resurfaced 221km of carriageways and reconstructed 263km of footways.
              Yet estimated preventative maintenance share fell from 10% (2021/22) to a projected 8% (2025/26),
              while pothole fills rose 17.6% from the 2022/23 low. The council plans to surface-dress approximately
              200km and resurface 80km in 2025/26 — against a 4,101km network.
            </p>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Worcestershire says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A roads:</strong> annual SCANNER surveys via contractor</li>
                <li>• <strong>B and C roads:</strong> annual SCANNER surveys via contractor</li>
                <li>• <strong>U roads:</strong> annual CVI (Coarse Visual Inspection) surveys</li>
                <li>• <strong>Bridges:</strong> circa 1,000 bridge inspections planned for 2025/26</li>
                <li>• <strong>Works programmes:</strong> planned up to three years in advance on a rolling basis</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Safety inspections</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council uses a risk-based approach where inspection frequency is determined by asset type.
                Safety inspections identify and remedy safety defects; condition surveys assess network-level
                performance; and reported defects from the public feed into maintenance decisions.
              </p>
              <p className="text-surface-600 text-sm">
                The public can report issues online — engineers see reports in real time and post updates via a
                software interface linked to the council&apos;s defect database.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Worcestershire must
              maintain public highways. To defend a claim under <strong className="text-white">Section 58</strong>,
              it must show a reasonable system for inspecting and repairing the specific defect — not just publish
              green DfT scorecards.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your road on the annual CVI U-road survey cycle — and had deterioration been recorded?</li>
              <li>• Did the defect meet intervention criteria during routine safety inspections?</li>
              <li>• Were there prior reports (council portal, FixMyStreet) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
            </ul>
          </div>

          <QuoteCard
            quote="We also take into consideration other key factors such as number of reported defects, complaints, third-party claims, public satisfaction and road traffic accident data."
            source="Worcestershire County Council Local Highway Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Planned 2025/26 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Planned Work 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            What Worcestershire says it will deliver this financial year
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-emerald-600">80km</div>
              <div className="text-sm text-emerald-800 font-medium mt-1">Carriageway resurfacing</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">200km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Surface dressing</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">70km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Footway reconstruction</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">~16,000</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Potholes planned</div>
              <div className="text-xs text-amber-700 mt-1">Part of ~30,000 total defects</div>
            </div>
          </div>

          <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">Coverage maths</h3>
            <p className="text-surface-600 text-sm">
              Even at 80km resurfacing plus 200km surface dressing, Worcestershire would treat roughly 6.8% of its
              4,101km carriageway network in a single year. The remaining 93%+ relies on reactive patching, routine
              inspections and annual condition surveys to catch deterioration — on a network where 22% of U-roads were
              already in red condition at the last CVI survey.
            </p>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Green-Rated County
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Worcestershire is not Waltham Forest — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN overall, spend and best-practice DfT ratings
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Capital spend ~2.5× DfT allocation (£50.5m vs £21.1m in 2024/25)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> B/C roads at 71% green condition; over 75% of potholes get cut-and-patch repairs
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual SCANNER surveys on classified roads; risk-based asset management via Ringway contract
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 800+ km surface-dressed since 2021 as preventative maintenance
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a well-prepared Section 58 defence. Generic &ldquo;council neglect&rdquo; arguments will not land.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition scorecard from DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 2,069km of U-roads — 50.5% of network — assessed by CVI not SCANNER
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road red condition at 22% (455km) in 2024; up from 19% in 2021
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> A-road green condition fell from 74% to 67% in one year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 16,022 pothole repairs in 2024/25 — up 17.6% from 2022/23
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Preventative share fell from 10% to projected 8% despite council stating prevention is more effective
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a county with GREEN scorecards and £52m capital budgets, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond network surveys</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the CVI survey gap and 22% red condition are your strongest structural arguments</li>
              <li>• Whether a temporary repair failed — the council admits up to 25% of potholes may not get permanent cut-and-patch treatment</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Worcestershire&apos;s
              own transparency data where it helps you — without pretending the council is failing overall.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Worcestershire County Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when
            you reported it so we can reference it in your pack. For highways emergencies outside office hours,
            call <strong>01905 822822</strong>.
          </p>
          <a
            href="https://www.worcestershire.gov.uk/council-services/travel-and-highways/report-road-or-path-issue"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a road or path issue — worcestershire.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Worcestershire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A green-rated county demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No CVI U-road survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No prevention-decline context</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road red condition (455km) documented</li>
                <li>✅ AMBER condition scorecard cited</li>
                <li>✅ 16,022 pothole repairs in 2024/25 referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Worcestershire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=worcestershire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Worcestershire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.worcestershire.gov.uk/council-services/travel-and-highways/roads-pavements-verges-and-lighting/highways-performance/local-highway-maintenance-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Worcestershire County Council Local Highway Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
